import { CSSProperties, FC, useCallback, useEffect, useMemo, useRef } from 'react';
import HotTable from '@handsontable//react';
import { ColumnSettings, GridSettings } from 'handsontable/settings';
import { registerHOTModules, rowHeaderWithControlsRenderer, validators } from '@/support/hot';
import {
  isSampleData,
  SampleDefinitionRowData,
  SamplePlanState,
  useAddSamplePlanContext,
  usePlannedSample,
} from '@/components/Samples/AddSamplePlanProvider';
import { generateName } from '@/components/Samples/SampleDefinition.utils';
import { _isNil, _isNotBlank, _isNotEmpty, _noop, _notNil } from '@/littledash';
import { TeamGlossaryApiId } from '@/model/Glosary.model';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { SampleTypeSelector } from '@/components/Samples/SampleTypeSelector';
import { MetadataGlossaryV1 } from '@/model/Metadata.model';
import Handsontable from 'handsontable';
import {
  fromInVivoValidator,
  InVivoValidator,
  InVivoValidatorField,
  isHybridValidator,
} from '@/support/hotValidator.util';
import {
  timepointAfterPasteHandler,
  TimepointEditor,
  valueToArray,
} from '@/components/UI/SpreadSheet/editors/TimepointEditor';
import { multiSelectAfterPasteHandler, MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { CellChange, CellValue, ChangeSource } from 'handsontable/common';
import { generateTempApiId } from '@/utils/ApiId.utils';
import {
  SampleDefinitionApiId,
  SampleDetailType,
  TempSampleDefinitionApiId,
  TempSampleTypeId,
} from '@/model/SamplePlan.model';
import { renderToString } from 'react-dom/server';
import styles from './AddSamplePlanGroup.module.scss';

registerHOTModules();
TimepointEditor.register();

const nameInVivoValidatorGenerator =
  (samplePlanState: SamplePlanState): InVivoValidator =>
  async (value) => {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (trimmedValue.length === 0) {
        return { valid: false, message: 'Name is required' };
      }

      if (trimmedValue.length > 255) {
        return { valid: false, message: 'Must be 255 characters or less' };
      }

      const isUnique = samplePlanState.isUniqueDefinitionName(trimmedValue);
      if (!isUnique) {
        return { valid: false, message: 'Must be unique' };
      }

      return { valid: true };
    } else {
      return { valid: false, message: 'Must be a text value' };
    }
  };

const detailInVivoValidator: InVivoValidator = async (value, columnSettings) => {
  const required = columnSettings.required === true;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return { valid: false, message: 'Must be a valid number' };
    }
    if (value <= 0) {
      return { valid: false, message: 'Must be a positive number' };
    }
    return { valid: true };
  }

  const stringValue = `${value ?? ''}`;
  const hasValue = _isNotBlank(stringValue);

  if (required && !hasValue) {
    return { valid: false, message: 'This field is required' };
  }

  if (hasValue) {
    const numValue = Number(stringValue);
    if (!Number.isFinite(numValue)) {
      return { valid: false, message: 'Must be a valid number' };
    }
    if (numValue <= 0) {
      return { valid: false, message: 'Must be a positive number' };
    }
  }
  return { valid: true };
};

const calculateCollectionTimeInVivoValidator: InVivoValidator = async (value, columnSettings) => {
  if (_isNil(value)) {
    return { valid: true };
  }
  if (typeof value === 'boolean') {
    if (value) {
      const timepointsColumn = columnSettings.instance.propToCol('timepoints');
      const timepoints = columnSettings.instance.getDataAtCell(columnSettings.row, timepointsColumn);

      if (Array.isArray(timepoints) && timepoints.length > 0) {
        return { valid: true };
      }
      return { valid: false, message: 'Can only calculate collection time when 1 or more timepoints are configured.' };
    }
    return { valid: true };
  }
  return { valid: false, message: 'Must be a boolean value' };
};

const calculateCollectionTimeValidator = fromInVivoValidator(calculateCollectionTimeInVivoValidator);

const validationError = (prop: string | number): string => {
  if (typeof prop === 'string') {
    switch (prop) {
      case 'name':
        return 'must be unique and between 1 and 255 characters long.';
      case 'weight':
      case 'volume':
      case 'width':
      case 'length':
      case 'depth':
        return 'Must be a positive number.';
    }
  }
  return 'Invalid value.';
};

const hasSignificantData = (definition: SampleDefinitionRowData): boolean => {
  return Object.entries(definition).some(([key, value]) => {
    if (key === 'api_id' || key === 'name' || key === 'calculate_collection_time') {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'boolean') {
      return value === true;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) && value > 0;
    }
    if (typeof value === 'string') {
      return _isNotBlank(value);
    }

    return _notNil(value);
  });
};

const defaultTableSettings: GridSettings = {
  manualColumnResize: true,
  manualRowResize: true,
  height: 'auto',
  allowInsertRow: true,
  minSpareRows: 1,
  hiddenColumns: { columns: [0] }, // hide api_id column but we need HoT to know about it to track changes
  rowHeaders: true,
  autoColumnSize: { useHeaders: true },
  columnHeaderHeight: 50, // Fix header height to prevent expansion/collapse on scroll
};
const MetadataTitle: FC<{ metadataGlossary: MetadataGlossaryV1 }> = ({ metadataGlossary }) => (
  <span>
    {metadataGlossary.title}
    {metadataGlossary.required ? <span className="required ml1">(req.)</span> : null}
  </span>
);

const metadataTitle = (metadataGlossary: MetadataGlossaryV1) =>
  renderToString(<MetadataTitle metadataGlossary={metadataGlossary} />);
const metadataGlossaryToColumn = (metadataGlossary: MetadataGlossaryV1): ColumnSettings => {
  switch (metadataGlossary.field_type) {
    case 'numeric': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: 'numeric',
        required: metadataGlossary.required,
        validator: validators.metadata.number,
      };
    }
    case 'date': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: 'date',
        renderer: 'in-vivo-date',
        dateFormat: 'YYYY-MM-DD',
        required: metadataGlossary.required,
        validator: validators.metadata.date,
      };
    }
    case 'select': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: MultiSelectEditor.type,
        width: 200,
        isMultiSelect: false,
        options: metadataGlossary.options ?? [],
        required: metadataGlossary.required,
      };
    }
    case 'multi_select': {
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: MultiSelectEditor.type,
        width: 200,
        isMultiSelect: true,
        jsonEncoded: true,
        options: metadataGlossary.options ?? [],
        required: metadataGlossary.required,
      };
    }
    default:
      return {
        title: metadataTitle(metadataGlossary),
        data: metadataGlossary.api_id,
        type: 'text',
        validator: validators.metadata.text,
        required: metadataGlossary.required,
      };
  }
};

export const AddSamplePlanGroup: FC<{ type: TeamGlossaryApiId | TempSampleTypeId }> = ({ type }) => {
  const hotRef = useRef<HotTable | null>(null);
  const { samplePlanState, sampleGlossary, metadataGlossary, enable_collection_time_calculation } =
    useAddSamplePlanContext();

  const plannedSampleData = usePlannedSample(type);

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (_notNil(hot)) {
      hot.addHook('afterPaste', timepointAfterPasteHandler);
      hot.addHook('afterPaste', multiSelectAfterPasteHandler);

      return () => {
        try {
          if (!hot.isDestroyed) {
            hot.removeHook('afterPaste', timepointAfterPasteHandler);
            hot.removeHook('afterPaste', multiSelectAfterPasteHandler);
          }
        } catch (error) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to remove Handsontable paste hooks during cleanup', {
              cause: error,
              slug: 'hot-cleanup-error',
            })
          );
        }
      };
    }
  }, [hotRef]);

  const { plannedSampleCompositeId } = useMemo(() => {
    if (isSampleData(plannedSampleData)) {
      return {
        plannedSampleCompositeId: (
          (sampleGlossary.get(plannedSampleData.type)?.options?.details ?? []) as Array<SampleDetailType>
        )
          .map((detail) => `${detail}_${plannedSampleData[detail] ?? '<missing>'}`)
          .join('__'),
      };
    }
    return { plannedSampleCompositeId: null };
  }, [plannedSampleData, sampleGlossary]);

  const tableSettings = useMemo<GridSettings | null>(() => {
    if (_notNil(plannedSampleData) && _notNil(plannedSampleCompositeId) && isSampleData(plannedSampleData)) {
      const detailColumns = (
        (sampleGlossary.get(plannedSampleData.type)?.options?.details ?? []) as Array<SampleDetailType>
      ).reduce<Array<ColumnSettings>>((acc, detail) => {
        if (detail in plannedSampleData) {
          acc.push({
            title: `${detail.charAt(0).toUpperCase()}${detail.slice(1)} (${plannedSampleData[detail]})`,
            data: detail,
            readOnly: false,
            type: 'numeric',
            validator: fromInVivoValidator(detailInVivoValidator),
          });
        }
        return acc;
      }, []);
      const metadataColumns = Array.from(metadataGlossary.values())
        .sort((left, right) => (left?.title ?? '').localeCompare(right?.title ?? '', undefined, { numeric: true }))
        .reduce<Array<ColumnSettings>>((acc, glossary) => {
          return [...acc, metadataGlossaryToColumn(glossary)];
        }, []);
      const columns = [
        { data: 'api_id' }, // will be hidden by default
        {
          title: 'Name',
          width: 200,
          data: 'name',
          type: 'text',
          validator: fromInVivoValidator(nameInVivoValidatorGenerator(samplePlanState)),
        },
        {
          title: 'Timepoints',
          data: 'timepoints',
          type: TimepointEditor.type,
          allow_negative: true,
          allow_zero: true,
        },
        ...(enable_collection_time_calculation
          ? [
              {
                title: 'Calculate collection time',
                data: 'calculate_collection_time',
                type: 'checkbox',
                validator: calculateCollectionTimeValidator,
              },
            ]
          : []),
        ...detailColumns,
        ...metadataColumns,
      ];
      return {
        ...defaultTableSettings,
        columns,
      };
    }
    return null;
  }, [
    metadataGlossary,
    plannedSampleCompositeId,
    plannedSampleData,
    sampleGlossary,
    samplePlanState,
    enable_collection_time_calculation,
  ]);
  const afterChangeHook = useCallback(
    function (this: Handsontable, changes: CellChange[] | null, source: ChangeSource) {
      if (source === 'loadData') {
        setTimeout(() => this.validateCells(), 200);
        return;
      }
      const currentSampleData = samplePlanState.getPlannedSample(type);
      if (isSampleData(currentSampleData)) {
        const sourceData = (this.getSourceData() as Array<SampleDefinitionRowData>).slice(0, -1);
        const definitions: Array<SampleDefinitionRowData> = [];
        const apiIdUpdates: Array<[number, number, SampleDefinitionRowData['api_id']]> = [];
        const nameUpdates: Array<[number, number, string]> = [];
        const existingApiIds = new Set<SampleDefinitionApiId | TempSampleDefinitionApiId>();

        sourceData.forEach((definition, index) => {
          const apiIdSet = _isNotBlank(definition.api_id);
          const isDuplicate = apiIdSet && existingApiIds.has(definition.api_id);
          const api_id = apiIdSet && !isDuplicate ? definition.api_id : generateTempApiId('sdf');
          if (!apiIdSet || isDuplicate) {
            apiIdUpdates.push([index, 0, api_id]);
          }
          existingApiIds.add(api_id);

          if (!_isNotBlank(definition.name) && source === 'edit') {
            if (hasSignificantData(definition)) {
              const sampleTypeName = sampleGlossary.get(currentSampleData.type)?.title ?? '';
              const nameCol = this.propToCol('name');
              const autoName = generateName(sampleTypeName, definitions);
              nameUpdates.push([index, nameCol, autoName]);
              definition.name = autoName;
            }
          }

          definitions.push({
            ...definition,
            api_id,
            timepoints: valueToArray(definition.timepoints),
            calculate_collection_time:
              typeof definition.calculate_collection_time === 'boolean' ? definition.calculate_collection_time : false,
          });
        });
        samplePlanState.setSampleData(
          {
            ...currentSampleData,
            definitions,
          },
          undefined,
          'hot'
        );
        if (apiIdUpdates.length > 0 || nameUpdates.length > 0) {
          const incomingUpdates: Array<[number, number, CellValue]> =
            changes?.map((change) => [change[0], this.propToCol(change[1]), change[3]]) ?? [];
          window.requestAnimationFrame(() => {
            this.setDataAtCell([...apiIdUpdates, ...nameUpdates, ...incomingUpdates], source);
          });
        }

        // If timepoints changed, revalidate calculate_collection_time for affected rows
        if (enable_collection_time_calculation) {
          const timepointChanges = changes?.filter((change) => change[1] === 'timepoints') ?? [];
          if (_isNotEmpty(timepointChanges)) {
            const calcTimeCol = this.propToCol('calculate_collection_time');
            window.requestAnimationFrame(() => {
              timepointChanges.forEach((change) => {
                const row = change[0];
                this.validateCell(
                  this.getDataAtCell(row, calcTimeCol),
                  this.getCellMeta(row, calcTimeCol),
                  _noop,
                  'timepoint-change'
                );
              });
            });
          }
        }
      }
    },
    [samplePlanState, type, sampleGlossary, enable_collection_time_calculation]
  );

  const afterValidateHook = useCallback(
    function (
      this: Handsontable,
      isValid: boolean,
      value: CellValue,
      row: number,
      prop: string | number,
      source: ChangeSource
    ) {
      if (_notNil(this.getDataAtCell(row, 0))) {
        const column = this.propToCol(prop);
        const cell = this.getCell(row, column);
        if (_notNil(cell)) {
          if (isValid) {
            cell.removeAttribute('data-tooltip-id');
            cell.removeAttribute('data-type');
            cell.removeAttribute('data-tooltip-content');
            cell.classList.remove('htInvalid');
          } else {
            cell.setAttribute('data-tooltip-id', 'global-tooltip-id');
            cell.setAttribute('data-type', 'error');
            cell.setAttribute('data-tooltip-content', validationError(prop));
            cell.classList.add('htInvalid');
            const validator = this.getCellValidator(row, column);
            if (typeof validator === 'function' && isHybridValidator(validator)) {
              validator[InVivoValidatorField](value, this.getCellMeta(row, column))
                .then((result) => {
                  if (result.valid) {
                    cell.removeAttribute('data-tooltip-id');
                    cell.removeAttribute('data-type');
                    cell.removeAttribute('data-tooltip-content');
                    cell.classList.remove('htInvalid');
                  } else {
                    cell.setAttribute('data-tooltip-content', result.message);
                    cell.classList.add('htInvalid');
                  }
                })
                .catch((error) => {
                  if (error instanceof DOMException && error.name === 'AbortError') {
                    ExceptionHandler.captureException(
                      new InVivoError('Cell validation aborted', {
                        cause: error,
                        slug: 'cell-validation-aborted',
                        level: 'debug',
                      })
                    );
                    cell.setAttribute('data-tooltip-content', 'Validation cancelled');
                    return;
                  }

                  ExceptionHandler.captureException(
                    new InVivoError('Cell validation failed unexpectedly', {
                      cause: error,
                      slug: 'cell-validation-error',
                      level: 'warning',
                    })
                  );
                  cell.setAttribute('data-tooltip-content', 'Invalid value.');
                });
            } else {
              cell.setAttribute('data-tooltip-content', 'Invalid value.');
            }
          }
        }
        samplePlanState.setValidation(type as TeamGlossaryApiId, `${row}.${prop}`, isValid);
        return isValid;
      }
      samplePlanState.setValidation(type as TeamGlossaryApiId, `${row}.${prop}`, true);
      return true;
    },
    [samplePlanState, type]
  );

  useEffect(() => {
    const abortController = new AbortController();

    samplePlanState.addEventListener(
      'duplicateSampleDefinitionNamesDetected',
      () => {
        const hot = hotRef.current?.hotInstance;
        if (_notNil(hot)) {
          hot.validateColumns([hot.propToCol('name')]);
        }
      },
      {
        signal: abortController.signal,
      }
    );
    return () => abortController.abort();
  }, [hotRef, samplePlanState]);

  const afterCreateRowHook = useCallback(
    function (this: Handsontable, index: number, amount: number, source?: ChangeSource) {
      const currentSampleData = samplePlanState.getPlannedSample(type);

      if (_notNil(currentSampleData) && isSampleData(currentSampleData)) {
        const sampleTypeName = sampleGlossary.get(currentSampleData.type)?.title ?? '';

        if ((source as string) === 'add-row.click' && index > 0) {
          const newApiId = generateTempApiId('sdf');
          const newName = generateName(sampleTypeName, currentSampleData.definitions);

          const colCount = this.countCols();
          const updates: Array<[number, number, CellValue]> = [];

          for (let col = 0; col < colCount; col++) {
            const prop = this.colToProp(col);
            if (prop === 'api_id') {
              updates.push([index, col, newApiId]);
            } else if (prop === 'name') {
              updates.push([index, col, newName]);
            } else {
              const value = this.getDataAtCell(index - 1, col);
              if (_notNil(value)) {
                updates.push([index, col, value]);
              }
            }
          }

          this.setDataAtCell(updates, source);
        }
      }

      if ((source as string) === 'add-row.click' || source === 'auto') {
        setTimeout(() => {
          this.validateCells();
        }, 100);
      }
    },
    [samplePlanState, type, sampleGlossary]
  );
  const afterRemoveRowHook = useCallback(
    function (this: Handsontable, index: number, amount: number, physicalRows: number[], source?: ChangeSource) {
      if ((source as string) === 'remove-row.click') {
        const currentSampleData = samplePlanState.getPlannedSample(type);
        if (isSampleData(currentSampleData)) {
          const data = {
            ...currentSampleData,
            definitions: this.getSourceData().slice(0, -1) as Array<SampleDefinitionRowData>,
          };
          samplePlanState.setSampleData(data, undefined, 'hot');
        }
        setTimeout(() => {
          this.validateCells();
        }, 100);
      }
    },
    [samplePlanState, type]
  );

  const MemoizedHotTable = useMemo(() => {
    const sampleData = samplePlanState.getPlannedSample(type);
    return (
      <HotTable
        className="ba b--gray"
        ref={hotRef}
        data={isSampleData(sampleData) ? [...(sampleData.definitions ?? []), {}] : [{}]}
        licenseKey={AppConfig.handsOnTableLicence}
        afterChange={afterChangeHook}
        afterValidate={afterValidateHook}
        afterCreateRow={afterCreateRowHook}
        afterRemoveRow={afterRemoveRowHook}
        afterGetRowHeader={rowHeaderWithControlsRenderer}
        settings={tableSettings ?? undefined}
      />
    );
  }, [
    samplePlanState,
    type,
    tableSettings,
    afterChangeHook,
    afterValidateHook,
    afterCreateRowHook,
    afterRemoveRowHook,
  ]);

  return (
    <div
      data-test-component="AddSamplePlanGroup"
      data-test-element="container"
      className="bg-near-white pa3 mv2"
      data-test-key={type}
    >
      <SampleTypeSelector type={type} />
      {_notNil(tableSettings) ? (
        <div>
          <div
            className={`ow-spreadsheet-styles mt4 ${styles.hotContainer}`}
            style={{ '--hot-column-height': `${tableSettings?.columnHeaderHeight ?? 50}px` } as CSSProperties}
            data-test-element="hot-container"
          >
            {MemoizedHotTable}
          </div>
        </div>
      ) : null}
    </div>
  );
};
